/**
 * Typed event emitter for interpreter → UI communication.
 *
 * The interpreter emits events; React components subscribe.
 */

/**
 * A minigame's exit delta — inventory changes to apply **once, outside the
 * sandbox**, on completion (combat design §6). Replaces the live inventory
 * bridge: the minigame tracks consumption locally, then hands back this delta.
 *
 * v1: `gained_items` is wired end-to-end (loot / pickups enter `_inventory`).
 * `consumed` / `ammo_deductions` are the subtract side — they only bite once the
 * loadout is drawn *from* the engine inventory (the deferred loadout menu); until
 * then the ids aren't in `_inventory`, so applying them is a no-op.
 */
export interface MinigameDelta {
  /** Item ids to add to the inventory (won loot, pickups). */
  gained_items?: string[];
  /** Item ids consumed during the run (removed if currently held). */
  consumed?: string[];
  /** Ammo/stackable spend by item id → count (removes up to N held instances). */
  ammo_deductions?: Record<string, number>;
}

export type EventMap = {
  /** A narration line is ready to display. */
  "dialogue.narrate": { template: string; resolved: string; id: string | null };
  /** A dialogue line with speaker is ready to display. */
  "dialogue.say": { who: string; template: string; resolved: string; color?: string | undefined; id: string | null };
  /** A choice menu should be displayed. */
  "choice.show": { options: ChoiceOption[] };
  /** A choice was selected by the player. */
  "choice.select": { index: number };
  /** The player advanced (clicked / pressed space). */
  "advance": undefined;
  /** The story reached -> END. */
  "story.end": undefined;
  /** A variable was updated. */
  "var.set": { name: string; value: unknown };
  /** A `~log` debug beat fired (dev console / overlay). */
  "debug.log": { template: string; resolved: string };
  /** A `~assert` beat failed (dev console / overlay). */
  "debug.assert": { message: string; passed: boolean };
  /** The game clock advanced — HUD should re-render (emitted once per advance). */
  "time.changed": { clock: number; display: string; partOfDay: string };
  /** Background change requested. */
  "bg.set": { asset: string; transition?: string | undefined; duration?: number | undefined; transition_color?: string | undefined; place_ref?: string | undefined };
  /** Animated/scrolling base layer set (rendered behind the background). */
  "base.set": { asset: string; mode?: string | undefined; scroll?: string | undefined; speed?: number | undefined; opacity?: number | undefined; loop?: boolean | undefined };
  /** Base layer cleared. */
  "base.clear": undefined;
  /** Place/pin an object sprite in-scene (asset already variant-resolved). */
  "object.show": { id: string; asset: string; x: number; y: number; layer: string };
  /** Re-skin an in-scene object sprite when its variant changes (no entry animation). */
  "object.update": { id: string; asset: string };
  /** Remove an object's in-scene sprite. */
  "object.hide": { id: string };
  /** Featured center-stage presentation of an object (dim + zoom). */
  "object.present": { id: string; asset: string; caption?: string | undefined; exit: string };
  /** Lay out a room's ambient objects on its shelves (sandbox room entry / change). */
  "object.room": {
    items: Array<{ id: string; asset: string; scale: number }>;
    shelves: Array<{ name: string; priority: number; z: string; ll: [number, number]; ur: [number, number] }>;
  };
  /** Sprite show/update requested. */
  "sprite.show": { id: string; asset: string; pos?: string | undefined; enter?: string | undefined; z?: number | undefined; scale?: number | undefined };
  /** Sprite hide requested. */
  "sprite.hide": { id: string; exit?: string | undefined; duration?: number | undefined };
  /** Audio play requested. */
  "audio.play": { channel: string; asset: string; loop?: boolean | undefined; fade_in?: number | undefined };
  /** Audio stop requested. */
  "audio.stop": { channel: string; fade_out?: number | undefined };
  /** Timed wait started. */
  "wait.start": { seconds: number };
  /** Timed wait completed. */
  "wait.end": undefined;
  /** Function call executed (P1b stub). */
  "call.exec": { func: string; args: unknown[] };
  /** Video playback requested. */
  "video.play": { asset: string; autoplay?: boolean | undefined; skip_after?: number | undefined };
  /** Video playback ended (finished or skipped). */
  "video.end": undefined;
  /** Screen effect requested. */
  "effect.run": { effect: string; intensity: number | undefined; duration: number | undefined; color: string | undefined };
  /** Sprite animation requested. */
  "animation.run": { id: string; anim: string; duration: number | undefined };
  /** Popup notification requested. */
  "popup.show": { text: string; icon: string | undefined; duration: number };
  /** An achievement was unlocked. */
  "achievement.unlock": { id: string; title: string; desc: string; icon: string | undefined };
  /** Cross-file scene load requested. */
  "scene.load": { program: string; entry?: string | undefined };
  /** Rollback: snapshot captured (for UI indicators). */
  "rollback.snapshot": { seq: number; depth: number };
  /** Rollback: rewind completed — UI should re-render. */
  "rollback.done": { seq: number };
  /** Save completed. */
  "save.done": { slotId: string };
  /** Load completed. */
  "load.done": { slotId: string };
  /** Load detected a program version mismatch. */
  "save.versionMismatch": { slotId: string; saveVersion: string; currentVersion: string };
  /** Phaser GameScene has finished create() and is listening for events. */
  "scene.ready": undefined;
  /** A new game is starting — GameScene should clear all visual state. */
  "game.reset": undefined;
  /** Interpreter entered sandbox mode. */
  "sandbox.enter": { place: string };
  /** Interpreter exited sandbox mode back to narrative. */
  "sandbox.exit": { scene: string };
  /** Player moved to a new room in sandbox mode. */
  "sandbox.move": { from: string; to: string };
  /** A verb was dispatched on an entity in sandbox mode. */
  "sandbox.verb": { entity: string; verb: string; result?: string };
  /** Sandbox narration text to display. */
  "sandbox.narrate": { text: string };
  /** Inventory changed (item taken, dropped, or assembled). */
  "sandbox.inventory": { action: "take" | "drop" | "assemble"; objectId: string; inventory: string[] };
  /** A minigame should be launched. */
  "minigame.start": { id: string; config: Record<string, unknown> };
  /** A minigame has completed (success, failure, or cancelled). */
  "minigame.complete": { id: string; result: string; delta?: MinigameDelta | undefined };
  /** A minigame encountered an error. */
  "minigame.error": { id: string; error: string };
};

export interface ChoiceOption {
  menu: string;
  story?: string | undefined;
  enabled: boolean;
  index: number;
}

type Handler<T> = T extends undefined ? () => void : (data: T) => void;

export class EventBus {
  private _handlers: Map<string, Set<Function>> = new Map();

  on<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): () => void {
    let set = this._handlers.get(event);
    if (!set) {
      set = new Set();
      this._handlers.set(event, set);
    }
    set.add(handler);

    // Return unsubscribe function
    return () => { set!.delete(handler); };
  }

  emit<K extends keyof EventMap>(
    event: K,
    ...args: EventMap[K] extends undefined ? [] : [EventMap[K]]
  ): void {
    const set = this._handlers.get(event);
    if (!set) return;
    for (const handler of set) {
      (handler as Function)(args[0]);
    }
  }

  /** Remove all handlers. */
  clear(): void {
    this._handlers.clear();
  }
}
